import { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Clock SVG Component
const ClockIcon = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// Default gallery items
const defaultItems = [
  {
    id: 'moment-1',
    title: 'Studio',
    description: 'Where prayers become paintings. A peaceful sanctuary of creativity and contemplation.',
    image_url: null,
    placeholder: 'Studio',
  },
  {
    id: 'moment-2',
    title: 'Process',
    description: 'The gentle embrace of dawn, filtering through the studio windows, illuminating works in progress.',
    image_url: null,
    placeholder: 'Process',
  },
  {
    id: 'moment-3',
    title: 'Exhibition',
    description: 'Community gatherings and exhibitions of faith, sharing the divine message through art.',
    image_url: null,
    placeholder: 'Exhibition',
  },
  {
    id: 'moment-4',
    title: 'Prayer',
    description: 'Moments of quiet reflection, where inspiration meets devotion.',
    image_url: null,
    placeholder: 'Prayer',
  },
];

export function Moments() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    fetchGalleryItems();
  }, []);

  const fetchGalleryItems = async () => {
    try {
      const response = await axios.get(`${API}/galleries/moments`);
      setItems(response.data.length > 0 ? response.data : defaultItems);
    } catch (error) {
      console.error('Error fetching gallery:', error);
      setItems(defaultItems);
    } finally {
      setLoading(false);
    }
  };

  const getImageUrl = (item) => {
    if (!item.image_url) return null;
    if (item.image_url.startsWith('http')) return item.image_url;
    return `${process.env.REACT_APP_BACKEND_URL}${item.image_url}`;
  };

  return (
    <div className="pt-20" data-testid="moments-page">
      {/* Hero Section */}
      <section className="py-24 bg-ocean-900 text-white relative border-t-8 border-ocean-800" data-testid="moments-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <h1 className="font-cinzel text-4xl md:text-5xl font-bold text-gold-400 mb-4 glowing-text">
              Moments of Grace
            </h1>
            <p className="text-ocean-200 font-light text-lg max-w-2xl mx-auto">
              Glimpses of divine inspiration, exhibitions, and the joyful process of creation.
            </p>
            <div className="mt-8 flex justify-center">
              <ClockIcon className="w-8 h-8 text-gold-500" />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-16">
            {/* Text Side */}
            <div className="space-y-6">
              <h3 className="font-cinzel text-2xl text-emerald-400 tracking-wide">Capturing the Light</h3>
              <p className="text-gray-300 leading-relaxed">
                Every piece begins with a quiet moment of reflection. Here, we share the journey of transforming faith into tangible beauty. From the first sketch in the quiet hours of the morning to the final touch of color, these moments are a testament to the Holy Spirit's gentle guidance.
              </p>
              <ul className="space-y-4 mt-6">
                <li className="flex items-start">
                  <span className="text-gold-500 mr-3 mt-1">✧</span>
                  <span className="text-gray-300">Studio glimpses and the intimate creative process.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold-500 mr-3 mt-1">✧</span>
                  <span className="text-gray-300">Community gatherings and exhibitions of faith.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-gold-500 mr-3 mt-1">✧</span>
                  <span className="text-gray-300">Testimonies of joy and spiritual healing through art.</span>
                </li>
              </ul>
            </div>
            
            {/* Decorative Grid */}
            <div className="grid grid-cols-2 gap-4 sm:gap-6">
              <div className="h-48 sm:h-56 bg-ocean-800 rounded-sm border border-gold-600/20 flex items-center justify-center overflow-hidden group shadow-lg">
                <span className="font-cinzel text-gold-500/40 group-hover:text-gold-400/90 transition duration-300 tracking-widest uppercase text-sm">Studio</span>
              </div>
              <div className="h-48 sm:h-56 bg-emerald-900/50 rounded-sm border border-gold-600/20 flex items-center justify-center overflow-hidden group translate-y-6 sm:translate-y-8 shadow-lg">
                <span className="font-cinzel text-gold-500/40 group-hover:text-gold-400/90 transition duration-300 tracking-widest uppercase text-sm">Process</span>
              </div>
              <div className="h-48 sm:h-56 bg-emerald-900/50 rounded-sm border border-gold-600/20 flex items-center justify-center overflow-hidden group -translate-y-6 sm:-translate-y-8 shadow-lg">
                <span className="font-cinzel text-gold-500/40 group-hover:text-gold-400/90 transition duration-300 tracking-widest uppercase text-sm">Exhibition</span>
              </div>
              <div className="h-48 sm:h-56 bg-ocean-800 rounded-sm border border-gold-600/20 flex items-center justify-center overflow-hidden group shadow-lg">
                <span className="font-cinzel text-gold-500/40 group-hover:text-gold-400/90 transition duration-300 tracking-widest uppercase text-sm">Prayer</span>
              </div>
            </div>
          </div>

          {/* Gallery Grid */}
          {!loading && items.some(i => getImageUrl(i)) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-16">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="bg-ocean-800/40 rounded-sm border border-gold-600/20 hover:border-gold-500/50 transition duration-300 cursor-pointer overflow-hidden"
                  onClick={() => setSelectedItem(item)}
                  data-testid={`moment-item-${item.id}`}
                >
                  <div className="h-48 bg-ocean-900 flex items-center justify-center relative overflow-hidden group">
                    {getImageUrl(item) ? (
                      <img 
                        src={getImageUrl(item)} 
                        alt={item.title} 
                        className="absolute inset-0 w-full h-full object-cover transition transform group-hover:scale-105 duration-500"
                      />
                    ) : (
                      <span className="font-cinzel text-gold-500/40 group-hover:text-gold-400/90 transition duration-300 tracking-widest uppercase text-sm">
                        {item.placeholder || item.title}
                      </span>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-ocean-900/80 via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h4 className="font-cinzel text-white text-sm">{item.title}</h4>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Detail Modal */}
      <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-ocean-900 border border-gold-600/30">
          <VisuallyHidden>
            <DialogTitle>{selectedItem?.title || 'Moment'}</DialogTitle>
            <DialogDescription>View details of the selected moment</DialogDescription>
          </VisuallyHidden>
          {selectedItem && (
            <div className="grid md:grid-cols-2">
              <div className="relative h-64 md:h-[500px] bg-ocean-800">
                {getImageUrl(selectedItem) ? (
                  <img
                    src={getImageUrl(selectedItem)}
                    alt={selectedItem.title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="font-cinzel text-gold-500/50 text-xl">{selectedItem.placeholder || selectedItem.title}</span>
                  </div>
                )}
              </div>
              <div className="p-8 flex flex-col justify-center">
                <span className="font-cinzel text-xs uppercase tracking-[0.2em] text-gold-400 mb-2">
                  Moments
                </span>
                <h2 className="font-cinzel text-2xl text-white mb-4">{selectedItem.title}</h2>
                <p className="font-lora text-emerald-100/80 leading-relaxed">{selectedItem.description}</p>
                <button
                  onClick={() => setSelectedItem(null)}
                  className="mt-8 btn-gold w-fit"
                  data-testid="moment-modal-close"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
